/* eslint-disable @repo/internal/react/no-class-components */

/* There is no good way to replace ErrorBoundary class components with a
 * functional component; currently no functional hook that can replace
 * 'componentDidCatch'.
 * Source: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * But I needed to get 'createAnalyticsEvent' via a hook hence
 * the wrapped class component.
 */

import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl-next';
import { useIntl } from 'react-intl-next';

import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import { UnhandledErrorScreen } from '@atlassian/generative-ai-modal/screens/UnhandledError';

import type { AIProactivePluginInjectionAPI } from '../../../aiProactivePluginType';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';

export const ModalRegionErrorBoundary = (props: {
	children: React.ReactNode;
	onCloseFallback: () => void;
}): React.JSX.Element => {
	const { api } = useContentComponentContext();

	const { formatMessage } = useIntl();

	return (
		<ExperienceApplicationErrorBoundaryClass
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			api={api}
			formatMessage={formatMessage}
		/>
	);
};

interface ErrorBoundaryProps {
	api: AIProactivePluginInjectionAPI;
	children: React.ReactNode;
	formatMessage: IntlShape['formatMessage'];
	onCloseFallback: () => void;
}

interface ErrorBoundaryState {
	errorCaptured: boolean;
	fallbackMessage?: string;
}

class ExperienceApplicationErrorBoundaryClass extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	private getFallbackMessage = (descriptor: MessageDescriptor) => {
		return this.props.formatMessage(descriptor);
	};

	state = {
		errorCaptured: false,
		fallbackMessage: this.getFallbackMessage(aiProactiveMessages.unhandledErrorMessage),
	};

	private shouldRecover() {
		return this.state.errorCaptured;
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		this.props.api?.analytics?.actions.fireAnalyticsEvent({
			eventType: EVENT_TYPE.OPERATIONAL,
			action: ACTION.UNHANDLED_ERROR_CAUGHT, //'unhandledErrorCaught',
			actionSubject: ACTION_SUBJECT.EDITOR_PLUGIN_AI, //'editorPluginAI',
			actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION, //'experienceApplication',
			attributes: {
				componentStack: errorInfo.componentStack || '',
				errorMessage: ErrorUtils.extractErrorMessage(error),
			},
		});
		this.setState({
			errorCaptured: true,
			fallbackMessage: this.getFallbackMessage(aiProactiveMessages.unhandledErrorMessage),
		});
	}

	render() {
		if (this.shouldRecover()) {
			return (
				<UnhandledErrorScreen
					text={this.state.fallbackMessage}
					onCancel={this.props.onCloseFallback}
				/>
			);
		}
		return this.props.children;
	}
}
